/**
 * ConnectionApprovalCard Component
 *
 * A clean, focused UI for selecting connections for new workflow actions.
 * Designed to be visually distinct from the diff card and minimize cognitive load.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Dropdown,
  Option,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  PlugConnectedRegular,
  WarningRegular,
  ArrowRightRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import type { ConnectionMatchingResult, ConnectionMatch } from '@/types/connectionProposal';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { useWorkflowConnectors } from '@/hooks/useWorkflowConnectors';
import { useTelemetry } from '@/hooks/useTelemetry';

// Fallback icon URLs for common connectors when manifest isn't loaded yet
const CONNECTOR_ICON_FALLBACKS: Record<string, { iconUri: string; brandColor: string }> = {
  azureblob: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/azureblob/icon.png',
    brandColor: '#0078D4',
  },
  office365: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/office365/icon.png',
    brandColor: '#D83B01',
  },
  sharepointonline: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/sharepointonline/icon.png',
    brandColor: '#036C70',
  },
  teams: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/teams/icon.png',
    brandColor: '#6264A7',
  },
  sql: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/sql/icon.png',
    brandColor: '#0078D4',
  },
  servicebus: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/servicebus/icon.png',
    brandColor: '#0078D4',
  },
  cosmosdb: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/documentdb/icon.png',
    brandColor: '#0078D4',
  },
  keyvault: {
    iconUri: 'https://connectoricons-prod.azureedge.net/releases/v1.0.1697/1.0.1697.3786/keyvault/icon.png',
    brandColor: '#0078D4',
  },
};

/**
 * Get fallback icon for a connector ID
 */
function getFallbackIcon(connectorId: string): { iconUri: string; brandColor: string } | undefined {
  if (!connectorId) return undefined;
  // Normalize: extract last segment and lowercase
  const parts = connectorId.split('/');
  const name = (parts[parts.length - 1] || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return CONNECTOR_ICON_FALLBACKS[name];
}

const useStyles = makeStyles({
  // Card with amber accent to differentiate from diff card
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    overflow: 'hidden',
    marginBottom: tokens.spacingVerticalS,
  },
  // Amber header for "needs attention" feel
  header: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorPaletteYellowBackground2,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerIcon: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontSize: tokens.fontSizeBase500,
  },
  headerText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  headerTitle: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  headerSubtitle: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  // Compact list of connection items
  body: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  // Single row item - action name + dropdown
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  itemResolved: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  itemWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
  },
  actionName: {
    flex: 1,
    fontWeight: tokens.fontWeightMedium,
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdown: {
    minWidth: '140px',
    maxWidth: '180px',
  },
  noConnectionText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteDarkOrangeForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  resolvedText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteGreenForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  // Simple footer with single action
  footer: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  footerHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  confirmButton: {
    minWidth: '100px',
  },
  skipLink: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: tokens.colorNeutralForeground2,
    },
  },
  cardResolved: {
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  headerResolved: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  headerIconResolved: {
    color: tokens.colorPaletteGreenForeground1,
  },
});

interface ConnectionApprovalCardProps {
  connectionMatching: ConnectionMatchingResult;
  onApprove: (requirementId: string, referenceKey: string) => void;
  onReject: (requirementId: string) => void;
  onApproveAll: () => void;
  onSkipAll: () => void;
}

export const ConnectionApprovalCard = ({
  connectionMatching,
  onApprove,
  onSkipAll,
}: ConnectionApprovalCardProps) => {
  const styles = useStyles();
  const { needsApproval, noConnectionAvailable } = connectionMatching;

  // Get connector visuals from the workflow connectors hook
  const { getConnectorInfo } = useWorkflowConnectors();
  const { trackEvent } = useTelemetry();

  // Track local selections for each proposal (keyed by proposal id)
  const [selections, setSelections] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    needsApproval.forEach((p) => {
      if (p.selectedConnection) {
        initial[p.id] = p.selectedConnection.referenceKey;
      }
    });
    return initial;
  });

  // Update selection for a proposal
  const handleSelectionChange = useCallback((proposalId: string, referenceKey: string) => {
    setSelections((prev) => ({ ...prev, [proposalId]: referenceKey }));
  }, []);

  // Confirm all current selections
  const handleConfirmAll = useCallback(() => {
    trackEvent('agent.chat.connection_approved');
    needsApproval.forEach((proposal) => {
      if (proposal.status === 'pending') {
        const selectedRef = selections[proposal.id];
        if (selectedRef) {
          onApprove(proposal.id, selectedRef);
        }
      }
    });
  }, [needsApproval, selections, onApprove, trackEvent]);

  // Calculate status
  const pendingItems = useMemo(
    () => needsApproval.filter((p) => p.status === 'pending'),
    [needsApproval]
  );
  const resolvedItems = useMemo(
    () => needsApproval.filter((p) => p.status === 'approved' || p.status === 'rejected'),
    [needsApproval]
  );
  const allPendingHaveSelections = pendingItems.every((p) => selections[p.id]);

  // If nothing to show, return null
  if (needsApproval.length === 0 && noConnectionAvailable.length === 0) {
    return null;
  }

  // All resolved - show compact summary
  if (pendingItems.length === 0 && resolvedItems.length > 0) {
    return (
      <div className={mergeClasses(styles.card, styles.cardResolved)}>
        <div className={mergeClasses(styles.header, styles.headerResolved)}>
          <CheckmarkCircleRegular className={mergeClasses(styles.headerIcon, styles.headerIconResolved)} />
          <div className={styles.headerText}>
            <Text className={styles.headerTitle} weight="semibold">
              Connections configured
            </Text>
            <Text className={styles.headerSubtitle}>
              {' '}{resolvedItems.filter((r) => r.status === 'approved').length} connection(s) assigned
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {/* Header with clear instruction */}
      <div className={styles.header}>
        <PlugConnectedRegular className={styles.headerIcon} />
        <div className={styles.headerText}>
          <Text className={styles.headerTitle} weight="semibold">
            Select connections
          </Text>
        </div>
      </div>

      {/* Compact list of items */}
      <div className={styles.body}>
        {/* Items that have compatible connections */}
        {needsApproval.map((proposal) => {
          const isResolved = proposal.status === 'approved' || proposal.status === 'rejected';
          const currentSelection = selections[proposal.id] || '';
          const selectedConn = proposal.compatibleConnections.find(
            (c) => c.referenceKey === currentSelection
          );
          // Get connector visuals - try all compatible connections to find one with icon data
          let iconUri: string | undefined;
          let brandColor: string | undefined;
          for (const conn of proposal.compatibleConnections) {
            const info = getConnectorInfo(conn.referenceKey);
            if (info?.iconUri) {
              iconUri = info.iconUri;
              brandColor = info.brandColor;
              break;
            }
          }
          // Fallback to static icon map if hook didn't have the data
          if (!iconUri) {
            const fallback = getFallbackIcon(proposal.requirement.connectorId);
            if (fallback) {
              iconUri = fallback.iconUri;
              brandColor = fallback.brandColor;
            }
          }

          return (
            <div
              key={proposal.id}
              className={mergeClasses(styles.item, isResolved && styles.itemResolved)}
            >
              <ConnectorIcon
                size={18}
                {...(iconUri && { iconUri })}
                {...(brandColor && { brandColor })}
              />
              <Text className={styles.actionName} title={proposal.requirement.actionName}>
                {proposal.requirement.actionName}
              </Text>
              {isResolved ? (
                <span className={styles.resolvedText}>
                  <CheckmarkCircleRegular />
                  {proposal.selectedConnection?.displayName || 'Configured'}
                </span>
              ) : (
                <Dropdown
                  className={styles.dropdown}
                  placeholder="Select..."
                  size="small"
                  value={selectedConn?.displayName || ''}
                  selectedOptions={currentSelection ? [currentSelection] : []}
                  onOptionSelect={(_, data) => {
                    if (data.optionValue) {
                      handleSelectionChange(proposal.id, data.optionValue);
                    }
                  }}
                >
                  {proposal.compatibleConnections.map((conn: ConnectionMatch) => (
                    <Option key={conn.referenceKey} value={conn.referenceKey}>
                      {conn.displayName}
                    </Option>
                  ))}
                </Dropdown>
              )}
            </div>
          );
        })}

        {/* Items with no available connections */}
        {noConnectionAvailable.map((req) => {
          const fallbackIcon = getFallbackIcon(req.connectorId);
          return (
            <div key={req.actionName} className={mergeClasses(styles.item, styles.itemWarning)}>
              <ConnectorIcon
                size={18}
                {...(fallbackIcon?.iconUri && { iconUri: fallbackIcon.iconUri })}
                {...(fallbackIcon?.brandColor && { brandColor: fallbackIcon.brandColor })}
              />
              <Text className={styles.actionName} title={req.actionName}>
                {req.actionName}
              </Text>
              <span className={styles.noConnectionText}>
                <WarningRegular />
                No {req.connectorDisplayName} available
              </span>
            </div>
          );
        })}
      </div>

      {/* Simple footer - one main action */}
      {pendingItems.length > 0 && (
        <div className={styles.footer}>
          <span
            className={styles.skipLink}
            onClick={onSkipAll}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onSkipAll()}
          >
            Skip all
          </span>
          <Button
            appearance="primary"
            size="small"
            icon={<ArrowRightRegular />}
            iconPosition="after"
            className={styles.confirmButton}
            disabled={!allPendingHaveSelections}
            onClick={handleConfirmAll}
          >
            Confirm
          </Button>
        </div>
      )}
    </div>
  );
};
